enum Method {
  GET = 'get',
  POST = 'post',
}

export interface Endpoint {
  method: Method
  url: string
  secured?: boolean
}

const { GET, POST } = Method

const signingApiEndpoints: Record<string, Endpoint> = {
  requestContractSmsCode: {
    method: POST,
    url: '/fullyonline/application/contract/sms/send',
  },
  requestScoringSmsCode: {
    method: POST,
    url: '/fullyonline/application/scoring/sms/send',
  },
  requestPreContractSmsCode: {
    method: POST,
    url: '/fullyonline/application/pre-contract/sms/send',
  },
  verifyContractSmsCode: {
    method: POST,
    url: '/fullyonline/application/contract/sms/verify',
  },
  verifyScoringSmsCode: {
    method: POST,
    url: '/fullyonline/application/scoring/sms/verify',
  },
  verifyPreContractSmsCode: {
    method: POST,
    url: '/fullyonline/application/pre-contract/sms/verify',
  },
}

const openApiEndpoints: Record<string, Endpoint> = {
  createLog: {
    method: POST,
    url: '/unsecured/log/create', // This endpoint has prefix "/unsecured" because it's called directly in the file createLog.ts
  },
  finishOnline: {
    method: POST,
    url: '/fullyonline/client/finish-online',
  },
  hello: {
    method: GET,
    url: '/hello',
  },
  initClientCache: {
    method: POST,
    url: '/fullyonline/product/calculator/init',
  },
  saveClientInfo: {
    method: POST,
    url: '/fullyonline/client/info/commit',
  },
  saveClientAddresses: {
    method: POST,
    url: '/fullyonline/client/address/commit',
  },
  saveClientDetails: {
    method: POST,
    url: '/fullyonline/client/details/commit',
  },
  saveFinanceProfession: {
    method: POST,
    url: '/fullyonline/client/finance/profession/commit',
  },
  saveFinanceEmployer: {
    method: POST,
    url: '/fullyonline/client/finance/employer/commit',
  },
  saveFinanceIncome: {
    method: POST,
    url: '/fullyonline/client/finance/income/commit',
  },
  saveFinanceExpenses: {
    method: POST,
    url: '/fullyonline/client/finance/expenses/commit',
  },
  saveClientAdditionalInfo: {
    method: POST,
    url: '/fullyonline/client/additional-info/commit',
  },
  saveLoanPreConfigurator: {
    method: POST,
    url: '/fullyonline/recapitulation/loan/commit',
  },
  saveAccountNumber: {
    method: POST,
    url: '/fullyonline/application/penny-payment/commit',
  },
  initLoanConfigurator: {
    method: POST,
    url: '/fullyonline/client/loan/configurator/init',
  },
  updateLoanConfigurator: {
    method: POST,
    url: '/fullyonline/client/loan/configurator/setup',
  },
  commitLoanConfiguratorRescoring: {
    method: POST,
    url: '/fullyonline/client/loan/configurator/commit-after-documents',
    secured: false,
  },
  getCodetableMaritalStatuses: {
    method: GET,
    url: '/codetable/cs/HCisStav',
  },
  getContract: {
    method: GET,
    url: '/fullyonline/product/contract',
  },
  getContractSignStatus: {
    method: GET,
    url: '/fullyonline/application/contract/sign/status',
  },
  getCodetableCountries: {
    method: GET,
    url: '/codetable/cs/HCisZemeAktualne',
  },
  getCodetableEmploymentTypes: {
    method: GET,
    url: '/codetable/cs/HCisPracovniPomerNAS',
  },
  getCodetableEducation: {
    method: GET,
    url: '/codetable/cs/HCisVzdelani',
  },
  getCodetableHousingTypes: {
    method: GET,
    url: '/codetable/cs/HCisDruhBydleni',
  },
  getOccupations: {
    method: POST,
    url: '/fullyonline/contact/job/list',
  },
  getPennyPayment: {
    method: GET,
    url: '/fullyonline/application/penny-payment',
  },
  postPreContractGenerate: {
    method: POST,
    url: '/fullyonline/product/pre-contract/generate',
  },
  getPreContractStatus: {
    method: GET,
    url: '/fullyonline/product/pre-contract/status',
  },
  getPreContract: {
    method: GET,
    url: '/fullyonline/product/pre-contract',
  },
  getEmployerHints: {
    method: POST,
    url: '/fullyonline/contact/company/hint',
  },
  getAddressHints: {
    method: POST,
    url: '/fullyonline/contact/address/hint',
  },
  getAvailability: {
    method: GET,
    url: '/fullyonline/availability',
  },
  getLoanRanges: {
    method: POST,
    url: '/fullyonline/recapitulation/loan/range/setup',
  },
  getSaveReturnUrl: {
    method: GET,
    url: '/fullyonline/application/savereturn/exit',
  },
  startClientScoring: {
    method: POST,
    url: '/fullyonline/application/scoring/create',
  },
  getListOfRequirements: {
    method: GET,
    url: '/fullyonline/client/list-of-requirements/setup',
  },
  getClientScoringStatus: {
    method: POST,
    url: '/fullyonline/application/scoring/status',
  },
  trackFinishInIb: {
    method: POST,
    url: '/fullyonline/client/finish-in-ib',
  },
  trackFinishOnBranch: {
    method: POST,
    url: '/fullyonline/client/finish-on-branch',
  },
  trackFinishToBusinessLoan: {
    method: POST,
    url: '/fullyonline/client/finish-to-business-loan',
  },
  getNeededDocs: {
    method: POST,
    url: '/fullyonline/client/needed-documents/info',
  },
  setupFinishOnBranch: {
    method: POST,
    url: '/fullyonline/client/finish-on-branch-url/setup',
  },
  testCacheExists: {
    method: POST,
    url: '/fullyonline/application/data/exists',
  },
  postMobileIdStart: {
    method: POST,
    url: '/fullyonline/mobile-id-upload/start',
  },
  postMobileIdResend: {
    method: POST,
    url: '/fullyonline/mobile-id-upload/resend',
  },
  getMobileIdStatus: {
    method: GET,
    url: '/fullyonline/mobile-id-upload/status',
  },
  postUploadPopFile: {
    method: POST,
    url: '/fullyonline/upload-pop/upload',
  },
  getUploadPopStart: {
    method: POST,
    url: '/fullyonline/upload-pop/start',
  },
  postPopProcessingSetup: {
    method: POST,
    url: '/fullyonline/application/pop/processing/setup',
  },
  getPopProcessingStatus: {
    method: GET,
    url: '/fullyonline/application/pop/processing/status',
  },
  getPopPostProcessingStatus: {
    method: GET,
    url: '/fullyonline/application/pop/post-processing/status',
  },
  createCif: {
    method: POST,
    url: '/fullyonline/client/cif/create',
  },
  dataLock: {
    method: POST,
    url: '/fullyonline/application/data/lock',
  },
  getCifStatus: {
    method: GET,
    url: '/fullyonline/client/cif/status',
  },
  pennyPaymentConfirm: {
    method: POST,
    url: '/fullyonline/application/penny-payment/confirm',
  },
  requestCacheSmsCode: {
    method: POST,
    url: '/fullyonline/application/savereturn/sms/send',
  },
  verifyCacheSmsCode: {
    method: POST,
    url: '/fullyonline/application/savereturn/sms/verify',
  },
}

export const localApiEndpoints: Record<string, string> = {
  appConfig: './config.json',
}

const apis = {
  openApi: {
    ...openApiEndpoints,
    ...signingApiEndpoints,
  },
  localApi: localApiEndpoints,
}

export default apis
